/* ============================================================
   FRAISE NOIRE — L'OUVERTURE

   Le sceau de la maison se dépose sur le papier, puis la feuille glisse et
   découvre la boutique. La silhouette n'est pas redessinée : c'est le logo
   officiel, intact, graines comprises. Ce qui est animé, c'est la MATIÈRE —
   un front d'encre freiné par un bruit fractal, qui imite la fibre du papier.

   RÈGLES DE DIFFUSION (arrêtées avec Hedy le 2026-10-03)
   · Une seule fois par visite : sessionStorage. Sans cela elle rejouerait à
     chaque clic sur un produit, et deviendrait insupportable au troisième.
   · Sur la première page ouverte, quelle qu'elle soit — accueil, rayon, fiche.
   · Y compris sur le trafic publicitaire, choix assumé : la marque frappe dès
     la première seconde.

   CE QUI NE DOIT JAMAIS ARRIVER
   La boutique ne dépend pas de l'animation. Le voile part de lui-même si le
   GPU refuse, si WebGL manque, si une texture ne charge pas, ou au bout de
   sept secondes quoi qu'il arrive. Il n'est même pas posé quand le visiteur a
   demandé moins d'animations dans ses réglages système.
   ============================================================ */
(() => {
  'use strict';

  const CLE = 'fn_ouverture_vue';
  const DUREE = 4200;
  const FENETRE = 4 * 60 * 60 * 1000;      // 4 h : la duree d'une meme visite

  /* Pourquoi un horodatage dans localStorage et non sessionStorage : ce dernier
     est propre a CHAQUE ONGLET. Un client qui ouvre un produit dans un nouvel
     onglet — le geste le plus courant sur une boutique — se reprenait
     l'ouverture en pleine figure. Ici elle ne rejoue qu'apres quatre heures,
     quel que soit l'onglet. */
  /* `?sceau` dans l'adresse force le rejeu : c'est le seul moyen de la revoir
     sans vider le cache du navigateur. Sans ce parametre, rien ne change pour
     un visiteur. */
  const force = /[?&]sceau\b/.test(location.search);
  if (!force) {
    try {
      const vu = parseInt(localStorage.getItem(CLE) || '0', 10);
      if (vu && Date.now() - vu < FENETRE) return;
    } catch (_) {}
  }
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const A = window.FN_OUVERTURE;
  if (!A) return;

  const voile = document.createElement('div');
  voile.className = 'sceau';
  voile.setAttribute('aria-hidden', 'true');
  voile.innerHTML =
    '<canvas class="sceau__toile"></canvas>' +
    '<span class="sceau__jour"></span><span class="sceau__fibre"></span><span class="sceau__filet"></span>';
  document.documentElement.classList.add('sceau-en-cours');
  (document.body || document.documentElement).prepend(voile);
  try { localStorage.setItem(CLE, String(Date.now())); } catch (_) {}

  let parti = false;
  function sortir(delai, raison) {
    if (parti) return; parti = true;
    if (raison) { try { console.warn('[ouverture]', raison); } catch (_) {} }
    setTimeout(() => {
      voile.classList.add('sceau--sort');
      document.documentElement.classList.remove('sceau-en-cours');
      setTimeout(() => voile.remove(), 1100);
    }, delai || 0);
  }
  setTimeout(() => sortir(0, 'garde-fou 7 s'), 7000);                      // garde-fou absolu

  const toile = voile.querySelector('.sceau__toile');
  const filet = voile.querySelector('.sceau__filet');
  const gl = toile.getContext('webgl', { antialias: true, alpha: false });
  if (!gl) { sortir(0, 'pas de webgl'); return; }

  const VS = 'attribute vec2 p;varying vec2 uv;void main(){uv=p*.5+.5;gl_Position=vec4(p,0.,1.);}';

  const FS = [
    'precision highp float;',
    'varying vec2 uv;',
    'uniform sampler2D fraise, mot;',
    'uniform vec2 res, tailleF, tailleM, centreF, centreM;',
    'uniform float t, tMot, grain;',
    'float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}',
    'float bruit(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);',
    ' return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+vec2(1,1)),f.x),f.y);}',
    'float fbm(vec2 p){float v=0.,a=.5;for(int i=0;i<5;i++){v+=a*bruit(p);p*=2.03;a*=.5;}return v;}',
    /* une couche d'encre : le front avance dans le repère de l'élément, pour
       qu'un mot large se remplisse au même rythme qu'une petite forme. */
    'float couche(sampler2D tex, vec2 u, vec2 centre, vec2 taille, float avance, vec2 depart){',
    ' vec2 c=(u-centre)/taille+.5;',
    ' if(c.x<0.||c.x>1.||c.y<0.||c.y>1.) return 0.;',
    ' float f1=fbm(u*46.)-.5, f2=fbm(u*150.+7.)-.5;',
    ' vec2 bave=vec2(f1+f2*.45,f2-f1*.4)*.0075;',
    ' float forme=texture2D(tex,clamp(c+bave,0.,1.)).r;',
    ' if(forme<.004) return 0.;',
    ' float d=length((c-depart)*vec2(1.25,1.));',
    ' float retard=fbm(u*5.5)*.34+fbm(u*17.)*.13;',
    ' float front=avance*1.95-d*1.15-retard;',
    ' float encre=smoothstep(0.,.085,front);',
    ' float frange=smoothstep(-.05,.16,front);',
    ' float grainBord=step(.46+(1.-frange)*.5, fbm(u*115.));',
    ' encre=max(encre,frange*grainBord);',
    ' return forme*clamp(encre,0.,1.);}',
    'void main(){',
    ' vec2 u=uv; u.y=1.-u.y;',
    ' float a=couche(fraise,u,centreF,tailleF,t,vec2(.5,.08));',
    ' float b=couche(mot,u,centreM,tailleM,tMot,vec2(-.02,.5));',
    ' float encre=max(a,b);',
    ' float densite=.90+.10*fbm(u*55.);',
    ' vec3 couleurEncre=vec3(.055,.055,.06)*densite;',
    ' float halo=smoothstep(.0,.55,encre)-encre;',
    ' vec3 papier=vec3(.957,.945,.925)-halo*.055;',
    ' vec3 col=mix(papier,couleurEncre,encre);',
    ' col-=(h(uv*res)-.5)*grain;',
    ' gl_FragColor=vec4(col,1.);}'
  ].join('\n');

  function compiler(type, src) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { sortir(0, 'shader : ' + gl.getShaderInfoLog(s)); return null; }
    return s;
  }
  const vs = compiler(gl.VERTEX_SHADER, VS), fs = compiler(gl.FRAGMENT_SHADER, FS);
  if (!vs || !fs) return;

  const prog = gl.createProgram();
  gl.attachShader(prog, vs); gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { sortir(0, 'link : ' + gl.getProgramInfoLog(prog)); return; }
  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 3,-1, -1,3]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

  const U = n => gl.getUniformLocation(prog, n);

  function poserTexture(img, unite) {
    const tex = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0 + unite);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    /* RGBA et non LUMINANCE : ce dernier fait echouer le televersement sur
       certains navigateurs, et l'echec etait silencieux — le voile partait au
       bout d'une seconde sans rien avoir dessine. L'image est en niveaux de
       gris, le shader lit donc .r, qui porte la meme valeur. */
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }
  const charger = src => new Promise((ok, non) => {
    const i = new Image();
    i.onload = () => ok(i); i.onerror = non;
    i.src = src;
  });

  let W, H;
  function cadrer() {
    const d = Math.min(devicePixelRatio || 1, 2.5);
    W = toile.width  = Math.round(innerWidth  * d);
    H = toile.height = Math.round(innerHeight * d);
    gl.viewport(0, 0, W, H);
    gl.uniform2f(U('res'), W, H);
  }

  Promise.all([charger(A.fraise), charger(A.mot)]).then(([imgF, imgM]) => {
    if (parti) return;
    poserTexture(imgF, 0); poserTexture(imgM, 1);
    gl.uniform1i(U('fraise'), 0); gl.uniform1i(U('mot'), 1);
    cadrer();

    function tailles() {
      const ar = W / H;
      const lF = Math.min(.34, .46 / ar * .78);
      gl.uniform2f(U('tailleF'), lF, lF * (imgF.height / imgF.width) * ar);
      const lM = Math.min(.54, .80 / ar * .78);
      gl.uniform2f(U('tailleM'), lM, lM * (imgM.height / imgM.width) * ar);
      /* Sur telephone le sceau se compose dans le tiers haut : le bandeau de
         consentement couvre la moitie basse a la premiere visite, et c'est
         justement la que l'ouverture se joue. */
      const etroit = innerWidth < 760;
      gl.uniform2f(U('centreF'), .5, etroit ? .255 : .415);
      gl.uniform2f(U('centreM'), .5, etroit ? .445 : .735);
      filet.style.top = (etroit ? 52 : 100) + '%';
    }
    tailles();
    addEventListener('resize', () => { cadrer(); tailles(); }, { passive: true });

    const douce = x => (x < .5 ? 2*x*x : 1 - Math.pow(-2*x + 2, 2) / 2);
    const seg = (x, a, b) => Math.min(1, Math.max(0, (x - a) / (b - a)));
    const t0 = performance.now();

    (function image(now) {
      if (parti) return;
      const x = (now - t0) / DUREE;
      gl.uniform1f(U('t'),     douce(seg(x, .00, .50)));
      gl.uniform1f(U('tMot'),  douce(seg(x, .40, .82)));
      gl.uniform1f(U('grain'), .030 + .030 * (1 - seg(x, 0, .5)));
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      filet.style.width = (seg(x, .72, .95) * Math.min(innerWidth * .34, 300)) + 'px';
      if (x < 1) requestAnimationFrame(image);
      else sortir(260);
    })(performance.now());
  }).catch(err => sortir(0, 'textures : ' + err));
})();
